// Internal modules
import {
  getEmailAutofill,
  getGlobals,
  getLocale,
  getMerchantPrefilledFields,
  getToken,
  getUnifiedLoginContextData,
} from '@/lib/globals';
import { LOG_PREFIX } from '../constants';
import { COMMON_PAYLOAD, STATE_NAME } from './fptiPayloads';
import type { GenericObjectType } from '@/types';
import { APP_MODE } from '@/types';
import type { CustomLoggerInitParams } from './types';
import type { LoggerType } from './logger';

/**
 * Logs the initial FPTI log and configures logger.fpti() with common payload
 * so these attrs get added to all FPTI payloads
 */
export const configureLoggerForFPTI = (
  logger: LoggerType,
  {
    token,
    mode = APP_MODE.SIGNUP,
    isWps,
    shipping_address_collection,
    shipping_preference,
    variant,
  }: CustomLoggerInitParams,
): void => {
  const { isBasl } = getGlobals();

  COMMON_PAYLOAD.context_id = token;
  COMMON_PAYLOAD.buyer_type = mode !== 'signup' ? 'GuestUser' : 'NewUser';
  const integrationType = isWps ? 'WPS' : 'EC';
  COMMON_PAYLOAD.context_type = `${integrationType}-token`;
  COMMON_PAYLOAD['x-paypal-fpti'] = {
    context_type: `${integrationType}-token`,
  };

  const { loginMethod } = getUnifiedLoginContextData();
  const { fptiCustType, fptiEmail } = getEmailAutofill();
  const fptiPrefilledFields = getMerchantPrefilledFields();

  const payload = {
    api_integration_type: 'standard',
    app_int_type: '', // BROWSER or WebView
    context_id: token,
    context_type: `${integrationType}-token`,
    cust_type: fptiCustType,
    design: 'full context',
    email: fptiEmail,
    experience: 'onboarding_lite',
    feed_name: 'checkoutuinodeweb',
    integration_type: integrationType,
    landing_page: isBasl ? 'BILLING' : 'LOGIN',
    login_type: loginMethod || (isBasl && 'BASL') || null,
    market: 'MERCHANT',
    merchant_prefill: fptiPrefilledFields,
    planning_activity_handle: '',
    shipping_address_collection,
    shipping_preference,
    state_name: STATE_NAME.init,
    token: token, // --> this is overwritten by the logger token builderPayload function
    transition_name: 'prepare_billing_page_init',
    variant,
  };

  logger.info('WEASLEY_FPTI_INIT_PAYLOAD', payload);

  logFPTI.call(logger, 'INIT', payload);
};

/**
 * Logs to FPTI. MUST be called with `this` context.
 * @param logger beaver-Logger wrapped instance
 * @param event FPTI event name
 * @param payload FPTI event payload
 * @param immediateFlush Optional, true if event needs to be flushed immediately
 */
// eslint-disable-next-line func-style
export function logFPTI(
  this: LoggerType, // for TS ONLY. Will be ignored by JS
  event: string,
  payload: GenericObjectType = {},
  immediateFlush?: boolean,
): void {
  const initialData = getGlobals();

  // Tack on common payload, required for every FPTI event,
  // and the current timestamp
  const integrationType =
    initialData.initialGqlData?.checkoutSession?.flags?.isWPS ||
    initialData.landingIWCData?.isWPS
      ? 'WPS'
      : 'EC';

  const token = getToken();

  COMMON_PAYLOAD.context_id = token;
  COMMON_PAYLOAD.context_type = `${integrationType}-token`;
  COMMON_PAYLOAD.integration_type = integrationType;
  COMMON_PAYLOAD.token = token;

  const finalPayload: GenericObjectType = {
    ...COMMON_PAYLOAD,
    ...payload,
    event_name: payload.event_name ?? event,
    server_epoch_timestamp: Date.now(),
    sessn_id: '',
    visitor_id: '',
  };

  // If the log is associated with an elmo feature
  if ('experiment_name' in payload) {
    const elmoExperiments =
      initialData.initialGqlData?.elmoExperimentWithRealTimeFilters
        ?.treatments ?? [];

    // We find the relevant experiment
    const relevantExperiment = elmoExperiments.find(
      experiment => experiment?.experimentName === payload.experiment_name,
    );

    if (relevantExperiment) {
      // And log the experimentId and treatmentId
      finalPayload.experimentation_experience = relevantExperiment.experimentId;
      finalPayload.experimentation_treatment = relevantExperiment.treatmentId;
    }
    delete finalPayload.experiment_name;
  }

  const xPaypalFptiStr = initialData['x-paypal-fpti'];

  if (xPaypalFptiStr) {
    try {
      const xPaypalFpti = JSON.parse(xPaypalFptiStr);

      finalPayload.sessn_id = xPaypalFpti.user_session_guid;
      finalPayload.visitor_id = xPaypalFpti.user_guid;
    } catch (error: any) {
      this.info('weasley_log_fpti_parse_xpaypalfpti_error', error);
    }
  }

  // Log to CAL for debugging
  const { country, locale } = getLocale();
  const action = LOG_PREFIX.test(event)
    ? `${event.toUpperCase()}_FPTI`
    : `WEASLEY_${event.toUpperCase()}_FPTI`;

  this.info(action, {
    clientCountry: country,
    clientLocale: locale,
    ...finalPayload,
  });

  // Fire off an FPTI event
  this.track(finalPayload);

  // FIXME: test this...
  if (immediateFlush) {
    this.flush();
  }
}
